import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, Home, Wifi, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../utils/api';

const OwnerPropertyForm = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

    // Stato unico per tutti i campi
    const [formData, setFormData] = useState({
        // General Info
        address: '',
        unitType: '', // es. Villa, Appartamento
        propertyType: '', // es. Intera, Stanza
        roomDetails: '', // Numero e Tipo di camere
        occupancy: '',
        dimensions: '',

        // Operations
        cleaningInstructions: '',
        ownerName: '',
        licenseNumber: '',
        wasteInstructions: '',
        parkingInstructions: '',

        // Tech & Manuals
        wifiName: '',
        wifiPassword: '',
        houseManual: '',

        // Listing Content
        title: '',
        summary: '',
        space: '',
        amenities: '',
        guestAccess: '',
        aroundArea: '',
        notes: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setStatus('idle');

        try {
            // Nota: Assicurati che il backend abbia un endpoint POST /content/property-submission
            // Se non esiste ancora, questo fallirà, ma la struttura frontend è corretta.
            await api.post('/content/property-submission', formData);
            setStatus('success');
            window.scrollTo(0, 0);
        } catch (error) {
            console.error("Error submitting property", error);
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    if (status === 'success') {
        return (
            <div className="min-h-screen pt-32 pb-20 px-6 flex flex-col items-center justify-center text-center max-w-2xl mx-auto animate-fade-in">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-4">Submission Received</h1>
                <p className="text-gray-500 mb-8">
                    Thank you, {formData.ownerName}. Your property details have been successfully submitted to our team. We will review the information and contact you shortly.
                </p>
                <button onClick={() => navigate('/')} className="bg-brand text-white px-8 py-3 rounded-xl font-bold hover:bg-black transition-colors">
                    Back to Home
                </button>
            </div>
        );
    }

    return (
        <div className="pt-32 pb-24 px-4 md:px-8 max-w-4xl mx-auto animate-fade-in font-sans">
            <div className="text-center mb-12">
                <span className="text-brand font-bold text-sm tracking-widest uppercase mb-2 block">Owner Onboarding</span>
                <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">Submit Property Details</h1>
                <p className="text-gray-500">Please fill out the form below to register your property information.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-10">

                {/* 1. Dati Principali */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                        <Home className="w-5 h-5 text-brand" /> Property Overview
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Full Address</label>
                            <input type="text" name="address" required value={formData.address} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="Street, City, Zip" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Owner Name</label>
                            <input type="text" name="ownerName" required value={formData.ownerName} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">License / Registration #</label>
                            <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Unit Type</label>
                            <input type="text" name="unitType" value={formData.unitType} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="e.g. Villa, Apartment" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Property Type</label>
                            <input type="text" name="propertyType" value={formData.propertyType} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="e.g. Entire Place" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Room Details</label>
                            <input type="text" name="roomDetails" value={formData.roomDetails} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="e.g. 3 Bedrooms (2 King, 1 Twin)" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Occupancy (Max Guests)</label>
                            <input type="number" name="occupancy" value={formData.occupancy} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Property Size (sq ft/m²)</label>
                            <input type="text" name="dimensions" value={formData.dimensions} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                    </div>
                </div>

                {/* 2. Listing Content */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-brand" /> Listing Description
                    </h3>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Listing Title</label>
                            <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none font-serif font-bold" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Summary</label>
                            <textarea rows={3} name="summary" value={formData.summary} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="Brief overview..." />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">The Space</label>
                                <textarea rows={4} name="space" value={formData.space} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Guest Access</label>
                                <textarea rows={4} name="guestAccess" value={formData.guestAccess} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Amenities List</label>
                            <textarea rows={3} name="amenities" value={formData.amenities} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="Pool, Wifi, AC, etc." />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Around the Area</label>
                            <textarea rows={3} name="aroundArea" value={formData.aroundArea} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                    </div>
                </div>

                {/* 3. Operational Info */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                        <Wifi className="w-5 h-5 text-brand" /> Operations & Tech
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Wi-Fi Name (SSID)</label>
                            <input type="text" name="wifiName" value={formData.wifiName} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Wi-Fi Password</label>
                            <input type="text" name="wifiPassword" value={formData.wifiPassword} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">House Manual (Link or Text)</label>
                            <textarea rows={3} name="houseManual" value={formData.houseManual} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Cleaning Instructions</label>
                            <textarea rows={3} name="cleaningInstructions" value={formData.cleaningInstructions} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Waste Collection</label>
                            <textarea rows={3} name="wasteInstructions" value={formData.wasteInstructions} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Parking Instructions</label>
                            <textarea rows={3} name="parkingInstructions" value={formData.parkingInstructions} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1 ml-1">Internal Notes</label>
                            <textarea rows={2} name="notes" value={formData.notes} onChange={handleChange} className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand outline-none" placeholder="Any extra details..." />
                        </div>
                    </div>
                </div>

                {status === 'error' && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-2">
                        <AlertCircle className="w-5 h-5"/> Something went wrong. Please try again.
                    </div>
                )}

                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={loading} className="bg-brand text-white px-10 py-4 rounded-xl font-bold shadow-lg hover:bg-[#c42e40] transition-all flex items-center gap-3 disabled:opacity-50">
                        {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Save className="w-5 h-5"/>}
                        Submit Property
                    </button>
                </div>
            </form>
        </div>
    );
};

export default OwnerPropertyForm;